import React, { Suspense, useEffect, useState } from "react";
import { Modal, Spin } from "antd";
import { PlayCircleFilled, VideoCameraFilled } from "@ant-design/icons";

const VideoPlayer = React.lazy(() => import("./PlyrVideoPlayer"));

function formatDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return null;
  const total = Math.floor(seconds);
  const pad = value => String(value).padStart(2, "0");
  const minutes = pad(Math.floor(total / 60) % 60);
  const remainder = pad(total % 60);
  return total >= 3600 ? `${pad(Math.floor(total / 3600))}:${minutes}:${remainder}` : `${minutes}:${remainder}`;
}

class PlayerLoadBoundary extends React.Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    return this.state.failed ? <div role="alert">The video player could not load. Refresh the page and try again.</div> : this.props.children;
  }
}

export function ChatVideoPreview({ src, alt, onDimensions }) {
  const [thumbnail, setThumbnail] = useState(null);
  const [duration, setDuration] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let active = true;
    // Decode one still frame without ever calling play() or mounting a bubble player.
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.onloadedmetadata = () => {
      if (active) {
        onDimensions(video.videoWidth, video.videoHeight);
        setDuration({ src, label: formatDuration(video.duration) });
      }
    };
    video.onloadeddata = () => {
      if (!active || !video.videoWidth || !video.videoHeight) return;
      const scale = Math.min(1, 660 / Math.max(video.videoWidth, video.videoHeight));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
      canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
      try {
        canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
        setThumbnail({ src, url: canvas.toDataURL("image/jpeg", 0.8) });
      } catch { /* The open button remains available if thumbnail decoding fails. */ }
      video.removeAttribute("src");
      video.load();
    };
    video.src = src;
    return () => {
      active = false;
      video.onloadedmetadata = null;
      video.onloadeddata = null;
      video.removeAttribute("src");
      video.load();
    };
  }, [src, onDimensions]);
  return <>
    <button type="button" aria-label={`Open video${alt ? `: ${alt}` : ""}`} onClick={() => setOpen(true)}
      style={{ position: "relative", display: "block", width: "100%", height: "100%", padding: 0, border: 0, background: "#161717", cursor: "pointer", color: "white", overflow: "hidden" }}>
      {thumbnail?.src === src && <img draggable={false} src={thumbnail.url} alt={alt || "Video thumbnail"} style={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }} />}
      <PlayCircleFilled aria-hidden style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", fontSize: 44, filter: "drop-shadow(0 1px 4px #000)" }} />
      {duration?.src === src && duration.label && <span aria-label={`Video duration ${duration.label}`} style={{ position: "absolute", left: 8, bottom: 8, display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 5px", borderRadius: 4, background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 12, lineHeight: 1.4, fontVariantNumeric: "tabular-nums", pointerEvents: "none" }}>
        <VideoCameraFilled aria-hidden /><span>{duration.label}</span>
      </span>}
    </button>
    {open && <Modal open centered title={<span style={{ color: "#fafafa" }}>{alt || "Video"}</span>}
      onCancel={() => setOpen(false)} footer={null} destroyOnHidden width="calc(100vw - 24px)"
      style={{ maxWidth: 1100, paddingBottom: 0 }} zIndex={1600}
      closeIcon={<span style={{ color: "#fafafa" }}>×</span>}
      styles={{ content: { height: "calc(100dvh - 24px)", display: "flex", flexDirection: "column", background: "#161717", padding: 16 },
        header: { background: "#161717", flexShrink: 0 }, body: { flex: 1, minHeight: 0, overflowY: "auto", color: "#fafafa", display: "flex" } }}>
      <PlayerLoadBoundary><Suspense fallback={<div role="status" style={{ margin: "auto" }}><Spin /> Loading video player…</div>}>
        <VideoPlayer src={src} title={alt} />
      </Suspense></PlayerLoadBoundary>
    </Modal>}
  </>;
}
